import { useLang } from '@/i18n';
import { getSolarRecommand } from '@/service/solar-calculator';
import gtm from '@/utils/gtm';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import EcoDesignEventsBus from 'eco-design-micro/EcoDesignEventsBus';
import EcoImg from 'eco-design/base/eco-img';
import { ENUM_ECO_CART_EVENT, ItfEcoCartItem } from 'eco-design/elements/eco-cart/type.d';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import css from './index.module.scss';
import { langText } from './langText';

const SectionCard: React.FC = () => {
    const { t } = useLang('theProduct.sectionCard');
    const { locale = '' } = useRouter();
    const i18n = langText[locale] || langText['jp'];
    const [list, setList] = useState([]);
    const PRODUCT_CONFIG = {
        jp: {
            item1: { productId: '14692433133935', variantId: '51919145959791' },
            item2: { productId: '14689202667887', variantId: '51927572021615' },
        },
    };

    const getProductConfig = (itemName: any) => PRODUCT_CONFIG[locale]?.[itemName] || PRODUCT_CONFIG.jp[itemName];
    const items = [
        {
            title: t('item1_name'),
            texts: [t('item1_text1'), t('item1_text2')],
            productId: getProductConfig('item1').productId,
            variantId: getProductConfig('item1').variantId,
        },
        {
            title: t('item2_name'),
            texts: [t('item2_text1'), t('item2_text2'), t('item2_text3')],
            productId: getProductConfig('item2').productId,
            variantId: getProductConfig('item2').variantId,
        },
    ];

    const getAsynItem = async (productId: string, variantId: string) => {
        return await new Promise(async (resolve, reject) => {
            try {
                const res = await getSolarRecommand({ productId, variantId }, locale);
                if (res?.code === '0') {
                    return resolve(res.data);
                } else {
                    console.error(res?.message);
                    return reject([]);
                }
            } catch (e) {
                console.error(e);
                return reject(e);
            }
        });
    };

    const addToCart = (info: any) => {
        if (Number(info?.inventoryQuantity || 0) > 0 || info?.inventoryPolicy === 'continue') {
            const { img, variantImg, productId, variantId, inventoryQuantity, inventoryPolicy, price, compareAtPrice, productTitle, skuTitle } = info;
            EcoDesignEventsBus.emit(ENUM_ECO_CART_EVENT.ECO_CART_ADD, [
                {
                    img,
                    variantImg,
                    productId,
                    variantId,
                    inventoryQuantity,
                    inventoryPolicy,
                    price,
                    compareAtPrice,
                    productTitle,
                    skuTitle,
                    quantity: 1,
                } as ItfEcoCartItem,
            ]);
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'bundle_add_to_cart',
            list_name: t('title'),
            product_name: t('title'),
            price: info.price,
            tag: 'none',
            quantity: 1,
            button_name: t('right_btn') || i18n.link_cart,
            __btn_id1: 'ef-swiper-card-bundle',
            __btn_id2: t('right_btn') || i18n.link_cart,
            __btn_type: 'common',
        });
    };

    useEffect(() => {
        const promises = items?.map((item: any) => getAsynItem(item?.productId, item.variantId));
        Promise.all(promises).then((res) => {
            const old = JSON.parse(JSON.stringify(items));
            const list = res.map((i: any, idx) => {
                old[idx] = { ...old[idx], ...i };
                old[idx].href =
                    idx === 0
                        ? `https://${locale}.ecoflow.com/products/wave-3-portable-air-conditioner?variant=${old[idx].variantId}`
                        : `https://${locale}.ecoflow.com/products/wave-3-wave-3-smart-extra-battery?variant=${old[idx].variantId}`;

                return old[idx];
            });
            setList(list || []);
            console.log('list', list, res);
        });
    }, []);

    return (
        <section
            className={`${css.section} WAVE-Section-Card GtmModuleData-section`}
            id="WAVE-Section-Card"
            data-section-id={'WAVE-Section-Card'}
            data-section-id2={t('title')}
        >
            <ul className="items">
                {list.map((item: any, idx) => (
                    <li className={`item item-${idx + 1}`} key={idx}>
                        <div className="card">
                            <div className="image-col">
                                <div className="image-box" aria-hidden="true">
                                    <EcoImg pc={item?.variantImg || item.img} />
                                </div>
                            </div>
                            <div className="info-col">
                                <h3 className="title">{item.title}</h3>
                                <ul className="bullets">
                                    {item.texts.map((txt: string, i: number) => (
                                        <li key={i} className="bullet">
                                            <span className="dot" />
                                            <span className="text">{txt}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="price-row">
                                    {item.price && (
                                        <span className="price">
                                            {i18n.price_prefix}
                                            {FormatMoneyLocal(item.price, locale)}
                                            {i18n.price_suffix}
                                        </span>
                                    )}
                                    {item.compareAtPrice && item.compareAtPrice !== item.price && (
                                        <span className="compare">
                                            {i18n.price_prefix}
                                            {FormatMoneyLocal(item.compareAtPrice, locale)}
                                            {i18n.price_suffix}
                                        </span>
                                    )}
                                </div>
                                <div className="actions">
                                    <a className="btn btn-primary" href={item?.href} target="_blank" rel="noreferrer">
                                        {t('left_btn')}
                                    </a>
                                    <a className="btn btn-outline" onClick={() => addToCart(item)}>
                                        {t('right_btn')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default SectionCard;
